import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Modal from '../../common/Modal';
import ModalButton from '../../common/ModalButton';
import SearchBox from '../../common/SearchBox';
import { capitalizeFirstLetter } from '../../../utils/string';
import { hasFinePointer } from '../../../utils/platform';
import { getBandInstrumentLabel } from '../../../utils/parts';

import { ReactComponent as CheckIcon } from '../../../icons/check.svg';
import { ReactComponent as PeopleIcon } from '../../../icons/group.svg';
import { ReactComponent as MusicIcon } from '../../../icons/music-double-note.svg';

import './AssignPartModal.scss';

const MEMBERS = 'members';
const INSTRUMENTS = 'instruments';

const AssignPartModal = ({ isOpen, onClose, part, teamUsers, instruments, onConfirm }) => {
  const { t } = useTranslation();
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedInstruments, setSelectedInstruments] = useState([]);
  const [soloInstruments, setSoloInstruments] = useState([]);
  const [tab, setTab] = useState(MEMBERS);
  const [search, setSearch] = useState('');
  // Snapshot of what was already assigned when the modal opened. The list is ordered off
  // this rather than off the live selection so toggling an instrument doesn't make it jump
  // out from under the cursor mid-click.
  const [pinnedInstruments, setPinnedInstruments] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setSelectedUsers(part?.assignedUsers ?? []);
      setSelectedInstruments(part?.assignedInstruments ?? []);
      setSoloInstruments(part?.soloInstruments ?? []);
      setPinnedInstruments(part?.assignedInstruments ?? []);
      setTab(MEMBERS);
      setSearch('');
    }
  }, [isOpen, part]);

  // Assigned instruments float to the top, everything else alphabetical by the name shown
  const sortedInstruments = useMemo(
    () =>
      [...instruments].sort((a, b) => {
        const pinned = pinnedInstruments.includes(b) - pinnedInstruments.includes(a);
        return pinned || getBandInstrumentLabel(a).localeCompare(getBandInstrumentLabel(b));
      }),
    [instruments, pinnedInstruments],
  );

  const query = search.trim().toLowerCase();
  const matchesSearch = (label) => !query || label.toLowerCase().includes(query);

  const filteredUsers = teamUsers.filter((u) => matchesSearch(u.name ?? ''));
  const filteredInstruments = sortedInstruments.filter((instrument) =>
    matchesSearch(getBandInstrumentLabel(instrument)),
  );

  const handleToggleUser = (user) => {
    setSelectedUsers((prev) => {
      const selected = prev.some((u) => u._id === user._id);
      return selected ? prev.filter((u) => u._id !== user._id) : [...prev, { _id: user._id, name: user.name }];
    });
  };

  const handleToggleInstrument = (instrument) => {
    setSelectedInstruments((prev) =>
      prev.includes(instrument) ? prev.filter((i) => i !== instrument) : [...prev, instrument],
    );
  };

  // Unticking a member drops their solo with them; an instrument's is dropped on save
  const handleToggleUserSolo = (user) => {
    setSelectedUsers((prev) =>
      prev.map((u) => (u._id === user._id ? { _id: u._id, name: u.name, ...(!u.solo && { solo: true }) } : u)),
    );
  };

  const handleToggleInstrumentSolo = (instrument) => {
    setSoloInstruments((prev) =>
      prev.includes(instrument) ? prev.filter((i) => i !== instrument) : [...prev, instrument],
    );
  };

  const handleTabChange = (nextTab) => {
    setTab(nextTab);
    setSearch('');
  };

  const handleSave = () => {
    onConfirm(part?.id ?? null, {
      assignedUsers: selectedUsers,
      assignedInstruments: selectedInstruments,
      soloInstruments: soloInstruments.filter((i) => selectedInstruments.includes(i)),
    });
    onClose();
  };

  const isMembers = tab === MEMBERS;

  // Both tabs render through the same list so a member row and an instrument row are
  // indistinguishable apart from their label
  const rows = isMembers
    ? filteredUsers.map((user) => {
        const selected = selectedUsers.find((u) => u._id === user._id);

        return {
          key: user._id,
          label: capitalizeFirstLetter(user.name),
          selected: !!selected,
          solo: !!selected?.solo,
          onToggle: () => handleToggleUser(user),
          onToggleSolo: () => handleToggleUserSolo(user),
        };
      })
    : filteredInstruments.map((instrument) => ({
        key: instrument,
        label: getBandInstrumentLabel(instrument),
        selected: selectedInstruments.includes(instrument),
        solo: soloInstruments.includes(instrument),
        onToggle: () => handleToggleInstrument(instrument),
        onToggleSolo: () => handleToggleInstrumentSolo(instrument),
      }));

  const emptyMessage =
    isMembers && teamUsers.length === 0
      ? t('dialogs.assignPart.noMembers')
      : isMembers
        ? t('dialogs.assignPart.noMembersMatch')
        : t('dialogs.assignPart.noInstrumentsMatch');

  const tabs = [
    { id: MEMBERS, label: t('dialogs.assignPart.members'), icon: <PeopleIcon />, count: selectedUsers.length },
    {
      id: INSTRUMENTS,
      label: t('dialogs.assignPart.instruments'),
      // Its viewBox is trimmed to the glyph, unlike group.svg — so it needs a smaller box to
      // come out the same optical size
      icon: <MusicIcon className="note-icon" />,
      count: selectedInstruments.length,
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('dialogs.assignPart.title')}
      className="AssignPartModal"
      evenFooter
      footer={
        <>
          <ModalButton variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </ModalButton>
          <ModalButton onClick={handleSave}>{t('common.save')}</ModalButton>
        </>
      }
    >
      <div className="tabs">
        {tabs.map(({ id, label, icon, count }) => (
          <button
            type="button"
            key={id}
            className={`tab${tab === id ? ' active' : ''}`}
            onClick={() => handleTabChange(id)}
          >
            {icon}
            <span className="tab-label">{label}</span>
            {count > 0 && <span className="tab-count">{count}</span>}
          </button>
        ))}
      </div>

      <SearchBox
        key={tab}
        autoFocus={hasFinePointer}
        value={search}
        label={isMembers ? t('dialogs.assignPart.searchMembers') : t('instruments.search')}
        onChange={setSearch}
        onClearInput={() => setSearch('')}
      />

      {rows.length === 0 ? (
        <p className="empty">{emptyMessage}</p>
      ) : (
        <ul className="list">
          {rows.map(({ key, label, selected, solo, onToggle, onToggleSolo }) => (
            <li key={key} className={`item${selected ? ' selected' : ''}`} onClick={onToggle}>
              <span className="name">{label}</span>
              {selected && (
                <span className="marks">
                  <button
                    type="button"
                    className={`solo${solo ? ' on' : ''}`}
                    aria-pressed={solo}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleSolo();
                    }}
                  >
                    {t('dialogs.assignPart.solo')}
                  </button>
                  <CheckIcon className="check" />
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
};

AssignPartModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  part: PropTypes.object,
  teamUsers: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
    }),
  ).isRequired,
  instruments: PropTypes.arrayOf(PropTypes.string).isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default AssignPartModal;
